import dayjs from 'dayjs';

import {i18n} from '@yourcoach/i18n';

import type {ConferenceApiModel} from '../../../../../api/conference';
import type {EventApiModel, NotificationEvent} from '../../../../../api/event';
import type {EventContextsMap, EventData} from '../types';

import {currentUserStore} from '../../../../../api/user';
import ChatIcon from '../../../../../assets/icons/secondary/Chat.svg';
import CameraIcon from '../../../../../assets/icons/secondary/Video.svg';
import {COURSE_PAUSED_CONFERENCE_DELETING_REASON} from '../../../../../services/entity-action/course/course.pause';
import {Text} from '../../../../../uikit/Text';
import {View} from '../../../../../uikit/View';
import {datetimeObjToISOString} from '../../../../../utils/datetime';
import {
  isConferenceEnded,
  isConferenceExpired,
  isConferenceStarted,
} from '../../../../../utils/entity/conference';
import {isChatConference} from '../../../../../utils/entity/conference/isChatConference';
import {getCourseLabel} from '../../../../../utils/entity/course';
import {getYcLogoFile} from '../../../../../utils/entity/file';
import {
  EXTRA_LONG_DATE_FORMAT,
  SHORT_DATE_FORMAT,
} from '../../../../book-conference/utils/constants';

// TODO: refactor this mess
export const getData = (
  event: NotificationEvent,
  contexts: EventContextsMap,
): EventData => {
  const {actor, conference} = contexts;

  // @ts-expect-error: TODO: describe why
  const isChatSession = isChatConference(conference);

  const data: EventData = {
    avatar: getYcLogoFile(180),
    avatarPlaceholder: i18n.t('shared:your_coach'),
    icon: isChatSession ? ChatIcon : CameraIcon,
    iconGradient: isChatSession ? 'info' : 'danger',
  };

  if (conference) {
    const courseLabel = getCourseLabel(conference.course);

    data.avatar = conference.course?.program?.avatar;
    data.avatarPlaceholder = conference.course?.program?.title;

    const isCoach = conference.coach_ids?.includes(
      currentUserStore.user?._id || '',
    );

    let createdBy: 'intro_call' | ConferenceApiModel['created_by'] =
      conference.created_by || 'program';

    let speaker = conference.coaches?.length
      ? conference.coaches[0]!.name
      : 'User not found';

    if (conference.created_by === 'client') {
      if (!conference.course_id) {
        createdBy = 'intro_call';
      }

      if (isCoach) {
        const client = conference.course?.client || conference.client;

        speaker = client?.name || 'User not found';
        data.avatar = client?.avatar;
        data.avatarPlaceholder = speaker;
      }

      if (!isCoach && conference.coaches?.length) {
        data.avatar = conference.coaches[0]!.avatar;
        data.avatarPlaceholder = conference.coaches[0]!.name;
      }
    }

    let conferenceTypeSplit = '';

    if (
      (
        [
          'conference_scheduled',
          'conference_rescheduled',
          'conference_pending',
          'conference_upcoming',
          'conference_taken',
          'conference_upcoming_5min',
          'conference_started',
          'user_canceled_session',
        ] as EventApiModel['type'][]
      ).includes(event.type)
    ) {
      conferenceTypeSplit = isChatSession ? 'chat' : 'live';
    }

    const startDate = dayjs(
      datetimeObjToISOString(conference.start_date),
    ).format(EXTRA_LONG_DATE_FORMAT);
    const endDate = dayjs(datetimeObjToISOString(conference.end_date)).format(
      SHORT_DATE_FORMAT,
    );

    const conferenceDateTime = `${startDate} - ${endDate}`;

    if (
      (
        [
          'conference_scheduled',
          'conference_rescheduled',
          'conference_taken',
        ] as EventApiModel['type'][]
      ).includes(event.type)
    ) {
      data.title =
        /**
         * @i18n_parser
         *
         * t('notifications:conference_scheduled.title.chat')
         * t('notifications:conference_scheduled.title.live')
         * t('notifications:conference_scheduled.title.intro_call')
         * t('notifications:conference_rescheduled.title.chat')
         * t('notifications:conference_rescheduled.title.live')
         * t('notifications:conference_rescheduled.title.intro_call')
         * t('notifications:conference_taken.title.chat')
         * t('notifications:conference_taken.title.intro_call')
         * t('notifications:conference_taken.title.live')
         */
        i18n.t(
          `notifications:${[event.type, 'title', conferenceTypeSplit]
            .filter(Boolean)
            .join('.')}`,
          {
            actorName: actor?.name,
            conferenceTitle: conference.title,
            date: conferenceDateTime,
            speaker,
          },
        );

      return data;
    }

    if (event.type === 'user_canceled_session' && conference.reason) {
      const reason =
        conference.reason === COURSE_PAUSED_CONFERENCE_DELETING_REASON
          ? i18n.t('shared:program_on_pause')
          : conference.reason;

      data.renderMessage = () => (
        <View style={{display: 'flex', flexDirection: 'column'}}>
          <Text variant="label.la" weight="semibold">
            {`${i18n.t('shared:reason')}:`}
          </Text>
          <Text variant="label.la">{reason}</Text>
        </View>
      );
    }

    /**
     * @i18n_parser
     *
     * t('notifications:conference_archived.title.coach')
     * t('notifications:conference_archived.title.client')
     * t('notifications:conference_archived.title.intro_call')
     * t('notifications:conference_archived.title.program')
     * t('notifications:conference_archives_shared.title.coach')
     * t('notifications:conference_archives_shared.title.client')
     * t('notifications:conference_archives_shared.title.intro_call')
     * t('notifications:conference_archives_shared.title.program')
     * t('notifications:conference_pending.title.coach.chat')
     * t('notifications:conference_pending.title.coach.live')
     * t('notifications:conference_pending.title.client.chat')
     * t('notifications:conference_pending.title.client.live')
     * t('notifications:conference_pending.title.intro_call.chat')
     * t('notifications:conference_pending.title.intro_call.live')
     * t('notifications:conference_pending.title.program.chat')
     * t('notifications:conference_pending.title.program.live')
     * t('notifications:conference_started.title.coach.chat')
     * t('notifications:conference_started.title.coach.live')
     * t('notifications:conference_started.title.client.chat')
     * t('notifications:conference_started.title.client.live')
     * t('notifications:conference_started.title.intro_call.chat')
     * t('notifications:conference_started.title.intro_call.live')
     * t('notifications:conference_started.title.program.chat')
     * t('notifications:conference_started.title.program.live')
     * t('notifications:conference_upcoming.title.client.chat')
     * t('notifications:conference_upcoming.title.client.live')
     * t('notifications:conference_upcoming.title.coach.chat')
     * t('notifications:conference_upcoming.title.coach.live')
     * t('notifications:conference_upcoming.title.intro_call.chat')
     * t('notifications:conference_upcoming.title.intro_call.live')
     * t('notifications:conference_upcoming.title.program.chat')
     * t('notifications:conference_upcoming.title.program.live')
     * t('notifications:conference_upcoming_5min.title.client.chat')
     * t('notifications:conference_upcoming_5min.title.client.live')
     * t('notifications:conference_upcoming_5min.title.coach.chat')
     * t('notifications:conference_upcoming_5min.title.coach.live')
     * t('notifications:conference_upcoming_5min.title.intro_call.chat')
     * t('notifications:conference_upcoming_5min.title.intro_call.live')
     * t('notifications:conference_upcoming_5min.title.program.chat')
     * t('notifications:conference_upcoming_5min.title.program.live')
     * t('notifications:user_canceled_session.title.client.chat')
     * t('notifications:user_canceled_session.title.client.live')
     * t('notifications:user_canceled_session.title.coach.chat')
     * t('notifications:user_canceled_session.title.coach.live')
     * t('notifications:user_canceled_session.title.intro_call.chat')
     * t('notifications:user_canceled_session.title.intro_call.live')
     * t('notifications:user_canceled_session.title.program.chat')
     * t('notifications:user_canceled_session.title.program.live')
     */
    data.title = i18n.t(
      `notifications:${[event.type, 'title', createdBy, conferenceTypeSplit]
        .filter(Boolean)
        .join('.')}`,
      {
        actorName: actor?.name,
        conferenceTitle: conference.title,
        courseDates: courseLabel ? `(${courseLabel})` : '',
        courseTitle: conference.course?.program?.title,
        date: conferenceDateTime,
        speaker,
      },
    );

    if (['conference_pending', 'conference_started'].includes(event.type)) {
      const _conferenceIsStarted = isConferenceStarted(
        conference as ConferenceApiModel,
      );
      const _conferenceIsEnded = isConferenceEnded(
        conference as ConferenceApiModel,
      );
      const _conferenceIsExpired = isConferenceExpired(
        conference as ConferenceApiModel,
      );

      data.buttons = [];

      if (!_conferenceIsEnded && !_conferenceIsExpired) {
        if (_conferenceIsStarted) {
          if (event.type === 'conference_started') {
            data.buttons.push({
              id: 'join_conference_button',
              label: isChatSession
                ? i18n.t('shared:go_to_chat')
                : i18n.t('shared:join'),
            });
          }
        } else if (
          isCoach &&
          dayjs() >=
            dayjs(datetimeObjToISOString(conference.start_date)).subtract(
              10,
              'minute',
            )
        ) {
          if (event.type === 'conference_pending') {
            data.buttons.push({
              id: 'start_conference_button',
              label: isChatSession
                ? i18n.t('shared:go_to_chat')
                : i18n.t(
                    'notifications:conference_pending.start_conference_button',
                  ),
            });
          }
        }
      }
    }
  }

  return data;
};
